import React, { useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { collect } from 'collect.js';
import { Button, Card, Input, Radio, Select, Switch } from 'antd';
import {
  DELIVERY_BRAND_BY_PROVIDER,
  DeliveryMode,
  DeliveryProvider,
  LAST_ORDER_GAP_OPTIONS,
  MIN_EXTERNAL_DELIVERY_SCHEDULE_AHEAD_MINUTES,
  ORDER_METHODS,
} from '../../services/exports/Constants';
import DeliveryZones from './delivery/DeliveryZones';
import usePermissions, { ManagerPermission } from '../../hooks/auth/usePermissions';
import TextArea from '../form/general/TextArea';
import DeliveryProvidersList from './DeliveryProvidersList';
import WoltDriveOnboardingCard from './WoltDriveOnboardingCard';
import {
  canManageWoltDriveOnboarding,
  deliveryProviderNotice,
  isDeliveryProviderProvisioned,
  needsWoltDriveOnboarding,
} from '../../services/helpers/WoltDriveOnboarding';

const HIDDEN_PROVIDERS = [DeliveryProvider.Standalone];

export default function DeliveryTab({ data, toggleMethodVisible, validator, feedback, loading, onChange, onSave }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Takeout:DeliveryTab' });

  const { company } = useSelector((state) => state.currentCompany);
  const { serviceProvider } = useSelector((state) => state.currentServiceProvider);

  const { can, isSuperAdmin, roles, isMasterProvider } = usePermissions();

  const regionalDeliveryProviders =
    serviceProvider.regional_config.supported_delivery_providers?.filter((item) => !HIDDEN_PROVIDERS.includes(item)) ??
    [];
  const hasDeliveryProvidersList = true;
  const supportedDeliveryProviders = collect([...data.delivery_providers])
    .push(...regionalDeliveryProviders)
    .unique()
    .toArray();
  // A provider without its per-restaurant credential stays listed but switched off, so the
  // manager sees why instead of the option silently missing (PROD-491).
  const isProviderConnected = (provider) => isDeliveryProviderProvisioned(company, provider);
  const getProviderNotice = (provider) => deliveryProviderNotice(company, provider);
  const connectedDeliveryProviders = supportedDeliveryProviders.filter(isProviderConnected);

  const showWoltDriveOnboarding =
    canManageWoltDriveOnboarding({ isSuperAdmin, roles, isMasterProvider }) && needsWoltDriveOnboarding(company);
  // The onboarding save replaces the list below with the saved one, so the card warns first.
  const hasUnsavedProviderChanges =
    JSON.stringify(data.delivery_providers ?? []) !== JSON.stringify(company?.delivery_providers ?? []);

  const deliveryZonesRef = useRef(null);

  const _onSave = async () => {
    await onSave();
    await deliveryZonesRef.current?.requestDeliveryZones();
  };

  return (
    <div className="mt-lg">
      <Card className="mb-md">
        <div className="text-base font-semibold mb-md">{t('sections.state.title')}</div>
        <div>
          <div className="mb-md flex items-center">
            <Switch
              id="delivery-visible"
              checked={!data.hidden_order_methods.includes(ORDER_METHODS.delivery)}
              onChange={toggleMethodVisible}
            />
            <label htmlFor="delivery-visible" className="my-auto ml-4 cursor-pointer">
              {t('sections.state.fields.delivery_visible.label')}
            </label>
          </div>
          <div className="mb-md flex items-center">
            <Switch
              id="has-delivery"
              checked={data?.has_delivery}
              onChange={(checked) => onChange('has_delivery', checked)}
            />
            <label htmlFor="has-delivery" className="my-auto ml-4 cursor-pointer">
              {t('sections.state.fields.has_delivery.label')}
            </label>
            <div className="text-sm text-gray-500">{feedback?.errors?.has_delivery}</div>
          </div>
          <div className="mb-md flex items-center">
            <Switch
              id="delivery-enabled"
              checked={data?.delivery_enabled}
              onChange={(checked) => onChange('delivery_enabled', checked)}
            />
            <label htmlFor="delivery-enabled" className="my-auto ml-4 cursor-pointer">
              {t('sections.state.fields.delivery_enabled.label')}
            </label>
            <div className="text-sm text-gray-500">{feedback?.errors?.delivery_enabled}</div>
          </div>
          <Button type="primary" loading={loading} disabled={loading} onClick={_onSave} className="my-4">
            {t('buttons.save')}
          </Button>
        </div>
      </Card>
      {showWoltDriveOnboarding && (
        // Keyed by restaurant: a venue ID verified for one must never be saveable on the next.
        <WoltDriveOnboardingCard
          key={company.id}
          hasUnsavedProviderChanges={hasUnsavedProviderChanges}
          onDeliveryProvidersChange={(providers) => onChange('delivery_providers', providers)}
        />
      )}
      {can(ManagerPermission.ManageExternalDelivery) &&
        serviceProvider.regional_config.supported_delivery_providers.length > 1 && (
          <Card className="mb-md">
            <div>
              <div className="text-lg font-medium mb-xs">{t('sections.delivery_mode.title')}</div>
              <div className="mb-md">
                {Object.values(DeliveryMode).map((item) => (
                  <div className="mb-2 flex relative" key={`delivery-mode-${item}`}>
                    <Radio
                      id={`has-${item}-delivery`}
                      checked={data.delivery_mode === item}
                      onChange={(e) => e.target.checked && onChange('delivery_mode', item)}
                      className="ml-0 absolute top-1/2 -translate-y-1/2 cursor-pointer"
                    />
                    <label htmlFor={`has-${item}-delivery`} className="ml-6 my-auto cursor-pointer">
                      {t(`sections.delivery_mode.fields.delivery_mode.options.${item}`, {
                        providers: collect(connectedDeliveryProviders)
                          .map((item) => DELIVERY_BRAND_BY_PROVIDER[item])
                          .implode(', '),
                      })}
                    </label>
                  </div>
                ))}
              </div>
              {hasDeliveryProvidersList && (
                <DeliveryProvidersList
                  data={data}
                  onChange={onChange}
                  supportedDeliveryProviders={supportedDeliveryProviders}
                  isProviderConnected={isProviderConnected}
                  getProviderNotice={getProviderNotice}
                />
              )}
              {data.delivery_mode === DeliveryMode.External && (
                <div className="mb-md">
                  <label htmlFor="external-delivery-pickup-note">
                    {t('sections.delivery_mode.fields.external_delivery_pickup_note.label')}
                  </label>
                  <TextArea
                    value={data.external_delivery_pickup_note}
                    onChange={(value) => onChange('external_delivery_pickup_note', value)}
                    limit={200}
                    placeholder={t('sections.delivery_mode.fields.external_delivery_pickup_note.placeholder')}
                  />
                </div>
              )}
              <Button type="primary" loading={loading} onClick={_onSave} className="my-4">
                {t('buttons.save')}
              </Button>
            </div>
          </Card>
        )}
      <Card className="mb-md">
        <div className="text-base font-semibold mb-md">{t('sections.delivery_info.title')}</div>
        <div>
          <div className="mb-md">
            <label htmlFor="average-order-delivery-time-min">
              {t('sections.delivery_info.fields.average_order_delivery_time_min.label')}
            </label>
            <div className="flex">
              <Input
                id="average-order-delivery-time-min"
                placeholder={t('sections.delivery_info.fields.average_order_delivery_time_min.placeholder')}
                type="number"
                value={data?.average_order_delivery_time_min ?? ''}
                onChange={(e) => onChange('average_order_delivery_time_min', e.target.value)}
                onWheel={(e) => e.target.blur()}
              />
              <label htmlFor="average-order-delivery-time-max" className="my-auto mx-2">
                {t('sections.delivery_info.fields.average_order_delivery_time_max.label')}
              </label>
              <Input
                id="average-order-delivery-time-max"
                placeholder={t('sections.delivery_info.fields.average_order_delivery_time_max.placeholder')}
                type="number"
                value={data?.average_order_delivery_time_max ?? ''}
                onChange={(e) => onChange('average_order_delivery_time_max', e.target.value)}
                onWheel={(e) => e.target.blur()}
              />
            </div>
            <div className="text-sm text-gray-500">
              {data?.has_delivery &&
                validator.message(
                  t('sections.delivery_info.fields.average_order_delivery_time_min.label'),
                  data?.average_order_delivery_time_min,
                  'required',
                )}
              {feedback?.errors?.average_order_delivery_time_min} {feedback?.errors?.average_order_delivery_time_max}
            </div>
          </div>
          <div className="mb-md">
            <span>
              <Trans t={t}>sections.scheduled_orders.fields.last_order_gap_delivery.label</Trans>
            </span>
            <Select
              id="last-order-gap-delivery"
              className="w-full"
              value={data?.last_order_gap_delivery ?? undefined}
              onChange={(value) => onChange('last_order_gap_delivery', value)}
              options={LAST_ORDER_GAP_OPTIONS.map((item) => ({
                value: item,
                label: t('sections.scheduled_orders.fields.last_order_gap_delivery.option', { value: item }),
              }))}
            />
          </div>
          <Button type="primary" loading={loading} disabled={loading} onClick={_onSave} className="my-4">
            {t('buttons.save')}
          </Button>
        </div>
      </Card>
      <Card className="mb-md">
        <div className="text-base font-semibold mb-md">{t('sections.scheduled_orders.title')}</div>
        <div>
          <div className="mb-md">
            <Switch
              id="has-scheduled-orders-delivery"
              checked={data?.has_scheduled_orders}
              onChange={(checked) => {
                if (!checked && !data?.has_asap_orders) {
                  return toast.error(t('toasts.at_least_one_order_method_required'));
                }

                return onChange('has_scheduled_orders', checked);
              }}
            />
            <label className="ml-4 align-bottom cursor-pointer" htmlFor="has-scheduled-orders-delivery">
              {t('sections.scheduled_orders.fields.has_scheduled_orders.label')}
            </label>
            <div className="text-sm text-gray-500">{feedback?.errors?.has_scheduled_orders}</div>
          </div>
          {data?.has_scheduled_orders && (
            <div className="mb-md">
              <span>
                <Trans t={t}>sections.scheduled_orders.fields.min_schedule_ahead_time_delivery.label</Trans>
              </span>
              <Input
                id="min-schedule-ahead-time-delivery"
                placeholder={t('sections.scheduled_orders.fields.min_schedule_ahead_time_delivery.placeholder')}
                type="number"
                min={
                  data.delivery_mode === DeliveryMode.External
                    ? MIN_EXTERNAL_DELIVERY_SCHEDULE_AHEAD_MINUTES
                    : undefined
                }
                value={data?.min_schedule_ahead_time_delivery ?? ''}
                onChange={(e) => onChange('min_schedule_ahead_time_delivery', e.target.value)}
                onWheel={(e) => e.target.blur()}
              />
              {data.delivery_mode === DeliveryMode.External && (
                <div className="text-sm text-gray-500">
                  {t('sections.scheduled_orders.fields.min_schedule_ahead_time_delivery.external_hint', {
                    minutes: MIN_EXTERNAL_DELIVERY_SCHEDULE_AHEAD_MINUTES,
                  })}
                  {validator.message(
                    t('sections.scheduled_orders.fields.min_schedule_ahead_time_delivery.validation_name'),
                    data?.min_schedule_ahead_time_delivery,
                    `min:${MIN_EXTERNAL_DELIVERY_SCHEDULE_AHEAD_MINUTES},num`,
                  )}
                </div>
              )}
            </div>
          )}
          <Button type="primary" loading={loading} disabled={loading} onClick={_onSave} className="my-4">
            {t('buttons.save')}
          </Button>
        </div>
      </Card>
      <Card className="mb-md">
        <div className="text-base font-semibold mb-md">{t('sections.delivery_zones.title')}</div>
        <div>
          <DeliveryZones ref={deliveryZonesRef} deliveryMode={data.delivery_mode} />
        </div>
      </Card>
    </div>
  );
}
